import { useEffect, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { fetchAccountResearchJobApi } from "@/features/accounts/api/accountsApi";
import { useMainStore } from "@/stores/index";
import { useEnrollmentBatch } from "@/features/campaigns/hooks/useCampaign";
import { useEnrollmentBatchToastStore } from "@/features/campaigns/stores/enrollmentBatchToastStore";
import {
  ACCOUNT_BATCH_STORAGE_KEY,
  dismissEnrollmentBatchToast,
  extractAccountResearchJobIds,
  isAccountResearchJobBatchId,
  openEnrollmentBatchToast,
  patchEnrollmentBatchToast,
  pickEnrollmentProgressFields,
} from "@/features/campaigns/utils/enrollmentBatchToast";
import { isEnrollmentBatchComplete } from "@/features/campaigns/utils/enrollmentContentProgress";

const STORAGE_PREFIX = "enrollmentBatch:";
const AUTO_DISMISS_MS = 5000;

const readStoredBatches = () => {
  if (typeof sessionStorage === "undefined") return [];

  const campaignBatches = Object.keys(sessionStorage)
    .filter(
      (key) =>
        key.startsWith(STORAGE_PREFIX) && key !== ACCOUNT_BATCH_STORAGE_KEY,
    )
    .map((key) => {
      try {
        return {
          storageKey: key,
          ...JSON.parse(sessionStorage.getItem(key) || "{}"),
        };
      } catch {
        return null;
      }
    })
    .filter((item) => item?.batchJobId)
    .sort((a, b) => {
      const aTime = Date.parse(a.createdAt || 0) || 0;
      const bTime = Date.parse(b.createdAt || 0) || 0;
      return bTime - aTime;
    });

  let accountBatch = null;
  try {
    const raw = sessionStorage.getItem(ACCOUNT_BATCH_STORAGE_KEY);
    if (raw) {
      accountBatch = JSON.parse(raw);
    }
  } catch {
    accountBatch = null;
  }

  const batches = [...campaignBatches];
  if (accountBatch?.batchJobId) {
    batches.push({ storageKey: ACCOUNT_BATCH_STORAGE_KEY, ...accountBatch });
  }

  return batches.sort((a, b) => {
    const aTime = Date.parse(a.createdAt || 0) || 0;
    const bTime = Date.parse(b.createdAt || 0) || 0;
    return bTime - aTime;
  });
};

const removeStoredBatch = (campaignId, batchJobId, batchMode) => {
  if (typeof sessionStorage === "undefined") return;
  if (batchMode === "account") {
    sessionStorage.removeItem(ACCOUNT_BATCH_STORAGE_KEY);
    return;
  }
  if (campaignId) {
    sessionStorage.removeItem(`${STORAGE_PREFIX}${campaignId}`);
  }
  if (batchJobId) {
    sessionStorage.removeItem(`${STORAGE_PREFIX}job:${batchJobId}`);
  }
};

export default function GlobalEnrollmentBatchTracker() {
  const {
    batchJobId,
    campaignId,
    mode,
    isComplete,
    isDismissed,
    setPatch,
  } = useEnrollmentBatchToastStore();

  const queryClient = useQueryClient();
  const { user } = useMainStore();
  const isAccountResearchJob = isAccountResearchJobBatchId(batchJobId);
  const accountResearchJobIds = useMemo(
    () => extractAccountResearchJobIds(batchJobId),
    [batchJobId],
  );

  const enrollmentBatch = useEnrollmentBatch({
    batchJobId,
    campaignId,
    mode,
    enabled: Boolean(batchJobId) && !isAccountResearchJob,
  });

  const accountJobQueries = useQueries({
    queries: accountResearchJobIds.map((jobId) => ({
      queryKey: ["account-research-job", jobId],
      queryFn: () => fetchAccountResearchJobApi({ userId: user?.id, jobId }),
      enabled: Boolean(isAccountResearchJob && user?.id),
      retry: false,
      refetchInterval: (query) => {
        const status = query?.state?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return 5000;
      },
    })),
  });
  const accountJobStatuses = accountJobQueries.map(
    (query) => query.data?.status,
  );
  const completedAccountJobs = accountJobStatuses.filter(
    (status) => status === "completed",
  ).length;
  const failedAccountJobs = accountJobStatuses.filter(
    (status) => status === "failed",
  ).length;
  const accountJobsComplete =
    accountResearchJobIds.length > 0 &&
    completedAccountJobs + failedAccountJobs === accountResearchJobIds.length;
  const hasMissingAccountJob = accountJobQueries.some(
    (query) => query.error?.response?.status === 404,
  );

  useEffect(() => {
    if (!isAccountResearchJob) return;
    if (accountJobsComplete) {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
    }
    patchEnrollmentBatchToast({
      accountDone: completedAccountJobs,
      failed: failedAccountJobs,
      isComplete: accountJobsComplete,
    });
  }, [
    accountJobsComplete,
    completedAccountJobs,
    failedAccountJobs,
    isAccountResearchJob,
    queryClient,
  ]);

  useEffect(() => {
    // Stale/unknown job (e.g. restored from storage after it was cleaned up):
    // drop the toast instead of polling forever.
    if (!isAccountResearchJob || !hasMissingAccountJob) return;
    if (hasMissingAccountJob) {
      removeStoredBatch(campaignId, batchJobId, "account");
      dismissEnrollmentBatchToast();
    }
  }, [isAccountResearchJob, hasMissingAccountJob, batchJobId, campaignId]);

  useEffect(() => {
    const [storedBatch] = readStoredBatches();
    if (storedBatch) {
      openEnrollmentBatchToast(storedBatch);
    }

    const onStarted = (event) => {
      if (event.detail?.batchJobId) {
        openEnrollmentBatchToast(event.detail);
      }
    };

    window.addEventListener("enrollmentBatchStarted", onStarted);
    return () =>
      window.removeEventListener("enrollmentBatchStarted", onStarted);
  }, []);

  useEffect(() => {
    const snapshot = enrollmentBatch.snapshot;
    if (!snapshot || !batchJobId) return;

    const store = useEnrollmentBatchToastStore.getState();
    const snapshotTotal = Number(snapshot.total || 0);
    const total =
      snapshotTotal > 0
        ? snapshotTotal
        : Math.max(Number(store.total || 0), snapshotTotal);
    const skipped = Number(snapshot.skipped || 0);
    const batchMode = snapshot.mode || mode || "campaign";
    const contentDone = Number(snapshot.contentDone || 0);
    const profileDone = Number(
      snapshot.profileDone ?? snapshot.researchDone ?? 0,
    );
    const accountDone = Number(snapshot.accountDone || 0);

    if (total <= 0 && skipped > 0) {
      removeStoredBatch(campaignId, batchJobId, batchMode);
      dismissEnrollmentBatchToast();
      return;
    }

    patchEnrollmentBatchToast({
      mode: batchMode,
      total,
      contentDone,
      profileDone,
      accountDone,
      failed: Number(snapshot.failed || 0),
      isComplete: isEnrollmentBatchComplete({
        total,
        contentDone,
        profileDone,
        accountDone,
        mode: batchMode,
      }),
    });
  }, [batchJobId, campaignId, enrollmentBatch.snapshot, mode]);

  const latestEvent = enrollmentBatch.status;
  const latestProgress = useMemo(
    () => latestEvent?.data ?? null,
    [latestEvent],
  );

  useEffect(() => {
    if (!latestEvent || !latestProgress) return;

    const { type } = latestEvent;

    if (type === "account.step") {
      if (latestProgress.status === "ready") {
        patchEnrollmentBatchToast({ incrementAccountDone: 1 });
      }
      return;
    }

    if (type === "profile.step" || type === "research.step") {
      if (latestProgress.status === "ready") {
        patchEnrollmentBatchToast({ incrementProfileDone: 1 });
      }
      return;
    }

    if (type === "content.step") {
      if (latestProgress.status === "ready") {
        patchEnrollmentBatchToast({ incrementContentDone: 1 });
      }
      return;
    }

    if (type === "batch.completed") {
      patchEnrollmentBatchToast({
        ...pickEnrollmentProgressFields(latestProgress),
        isComplete: true,
      });
      return;
    }

    if (type === "batch.progress" || type === "batch.snapshot") {
      const fields = pickEnrollmentProgressFields(latestProgress);
      const store = useEnrollmentBatchToastStore.getState();
      patchEnrollmentBatchToast({
        ...fields,
        isComplete: isEnrollmentBatchComplete({
          total: fields.total ?? store.total,
          contentDone: fields.contentDone ?? store.contentDone,
          profileDone: fields.profileDone ?? store.profileDone,
          accountDone: fields.accountDone ?? store.accountDone,
          mode: fields.mode ?? store.mode,
        }),
      });
    }
  }, [latestEvent, latestProgress]);

  useEffect(() => {
    if (!isComplete || !batchJobId) return undefined;

    removeStoredBatch(campaignId, batchJobId, mode);

    const timer = window.setTimeout(() => {
      dismissEnrollmentBatchToast();
    }, AUTO_DISMISS_MS);

    return () => window.clearTimeout(timer);
  }, [batchJobId, campaignId, isComplete, mode]);

  useEffect(() => {
    if (!batchJobId || !isDismissed) return;
    setPatch({ isDismissed: true });
  }, [batchJobId, isDismissed, setPatch]);

  return null;
}
